import { SpeakerWaveIcon } from '@heroicons/react/24/outline';
import Fuse from 'fuse.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import WaveSurfer from 'wavesurfer.js';

import { useGetArtifactDigestQuery } from '@/services/api/artifactDigestService';
import { useGetTranscriptSegmentsQuery } from '@/services/api/transcriptService';
import { ArtifactUnpacked } from '@/services/types/artifact-types';

import { ConversationAudioControls } from './ConversationAudioControls';
import { PreviewHeader } from './PreviewHeader';
import { SearchBar } from './SearchBar';
import { TranscriptList } from './TranscriptList';

interface AudioConversationPreviewProps {
  artifact: ArtifactUnpacked;
}

/**
 * AudioConversationPreview – Displays call metadata & transcript with rich search capabilities.
 * Extracted from artifact-inspector and rebuilt following component decomposition guidelines.
 */
export const AudioConversationPreview = ({
  artifact,
}: AudioConversationPreviewProps) => {
  const { matter_id } = useParams<{ matter_id: string }>();

  /* -------------------------------- Data Fetching ------------------------------- */
  const ac = artifact.audio_conversation || artifact.transcript?.conversation;

  const { data: digest } = useGetArtifactDigestQuery(artifact.id, {
    skip: !artifact.id,
  });

  const { data: transcriptSegments = [], isLoading } =
    useGetTranscriptSegmentsQuery(
      {
        matterId: matter_id || '',
        conversationId: ac?.id || '',
      },
      {
        skip: !matter_id || !ac?.id,
      },
    );

  /* -------------------------------- State & Refs -------------------------------- */
  const [searchQuery, setSearchQuery] = useState('');
  const [matchIds, setMatchIds] = useState<string[]>([]);
  const [matchRanges, setMatchRanges] = useState<
    Record<string, Array<[number, number]>>
  >({});
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);

  // Keep a map from segment id to element for scrolling
  const segmentRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Audio playback state
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [activeAudioSegmentId, setActiveAudioSegmentId] = useState<
    string | null
  >(null);
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);

  // A ref to throttle automatic sync right after manual seek (prevents UI glitch)
  const ignoreSyncUntilRef = useRef<number>(0);

  /* --------------------------------- Fuse Setup --------------------------------- */
  const fuse = useMemo(() => {
    return new Fuse(transcriptSegments, {
      keys: ['text'],
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
      ignoreLocation: true,
      minMatchCharLength: 3,
      findAllMatches: false,
    });
  }, [transcriptSegments]);

  /* ----------------------------- Search Side-Effects ----------------------------- */
  useEffect(() => {
    if (!searchQuery.trim()) {
      setMatchIds([]);
      setActiveMatchIndex(0);
      return;
    }

    const results = fuse.search(searchQuery);
    const resultIdSet = new Set(results.map((r) => r.item.id));
    // Preserve transcript order
    const orderedIds = transcriptSegments
      .filter((seg) => resultIdSet.has(seg.id))
      .map((seg) => seg.id);
    setMatchIds(orderedIds);

    const ranges: Record<string, Array<[number, number]>> = {};

    results.forEach((r) => {
      if (r.matches) {
        const textMatch = r.matches.find((m) => m.key === 'text');
        if (textMatch && textMatch.indices) {
          const filteredIndices = textMatch.indices
            .filter(([start, end]) => end - start >= 1)
            .sort(([a], [b]) => a - b);

          // Merge ranges that are close together (within 3 chars)
          const mergedRanges = filteredIndices.reduce<Array<[number, number]>>(
            (acc, [start, end]) => {
              const lastRange = acc[acc.length - 1];
              if (lastRange && start <= lastRange[1] + 3) {
                // Merge with previous range
                lastRange[1] = Math.max(lastRange[1], end);
              } else {
                acc.push([start, end]);
              }
              return acc;
            },
            [],
          );

          ranges[r.item.id] = mergedRanges;
        }
      }
    });

    setMatchRanges(ranges);
    setActiveMatchIndex(orderedIds.length ? 0 : 0);
  }, [searchQuery, fuse, transcriptSegments]);

  // Scroll to active match when it changes
  useEffect(() => {
    if (!matchIds.length) return undefined;
    const activeId = matchIds[activeMatchIndex];
    if (!activeId) return undefined;

    const scrollToElement = () => {
      const element = segmentRefs.current[activeId];
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
        return true;
      }
      return false;
    };

    // Try immediate scroll first
    if (scrollToElement()) return undefined;

    // If element not found, wait a bit for render and try again
    const timeoutId = setTimeout(() => {
      if (!scrollToElement()) {
        // Final attempt with longer delay
        setTimeout(scrollToElement, 100);
      }
    }, 16);

    return () => clearTimeout(timeoutId);
  }, [activeMatchIndex, matchIds]);

  /* ------------------------------- Helper Methods ------------------------------- */
  function seekToTime(seconds: number) {
    const ws = wavesurferRef.current;
    if (!ws) return;
    const duration = ws.getDuration();
    if (!duration) return;
    const progress = seconds / duration;
    ws.seekTo(progress);
  }

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const highlightText = useCallback((text: string, query: string) => {
    if (!query.trim()) return text;

    try {
      const regex = new RegExp(
        `(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`,
        'gi',
      );
      return text.replace(
        regex,
        '<mark class="bg-yellow-200 text-gray-900 rounded-sm px-0.5">$1</mark>',
      );
    } catch {
      return text;
    }
  }, []);

  /** Distinct speakers – used to deterministically assign colors */
  const speakers = useMemo(() => {
    return Array.from(
      new Set(
        transcriptSegments.map((s) => s.external_speaker_label || 'Speaker'),
      ),
    );
  }, [transcriptSegments]);

  const getSpeakerColor = (speaker: string) => {
    if (speakers.length <= 1) return 'bg-blue-100';
    const index = speakers.indexOf(speaker);
    return index % 2 === 0 ? 'bg-blue-100' : 'bg-purple-100';
  };
  const getSpeakerTextColor = (speaker: string) => {
    if (speakers.length <= 1) return 'text-blue-700';
    const index = speakers.indexOf(speaker);
    return index % 2 === 0 ? 'text-blue-700' : 'text-purple-700';
  };

  /* ------------------------------ Render Helpers ------------------------------ */
  const handleSegmentInteract = (
    segmentId: string,
    event?: React.KeyboardEvent,
  ) => {
    if (!event || event.key === 'Enter' || event.key === ' ') {
      const index = matchIds.indexOf(segmentId);
      if (index !== -1) setActiveMatchIndex(index);

      // Seek audio if available
      const segment = transcriptSegments.find((s) => s.id === segmentId);
      if (segment) {
        // Set active segment immediately for UI feedback
        setActiveAudioSegmentId(segment.id);
        // If audio is currently playing, momentarily pause time-sync updates so we don't flicker
        if (audioIsPlaying) {
          ignoreSyncUntilRef.current = Date.now() + 300; // 300ms buffer
        }
        seekToTime(segment.start_time);
      }

      if (event) event.preventDefault();
    }
  };

  const gotoPreviousMatch = () => {
    setActiveMatchIndex((current) => {
      if (current <= 0) {
        // Wrap to last match
        return matchIds.length - 1;
      }
      return current - 1;
    });
  };
  const gotoNextMatch = () => {
    setActiveMatchIndex((current) => {
      if (current >= matchIds.length - 1) {
        // Wrap to first match
        return 0;
      }
      return current + 1;
    });
  };

  /* ---------------------- Sync active segment with playback --------------------- */
  useEffect(() => {
    const ws = wavesurferRef.current;
    // Only update automatically when audio is actively playing. This prevents
    // unexpected segment jumps when a user clicks around while paused.
    if (!ws || !ws.isPlaying()) return;

    // Respect the ignore window after a manual seek event to avoid flicker
    if (ignoreSyncUntilRef.current > Date.now()) return;

    if (!transcriptSegments.length) return;

    let currentSegmentId: string | null = null;
    for (let i = transcriptSegments.length - 1; i >= 0; i -= 1) {
      if (transcriptSegments[i].start_time <= currentTime) {
        currentSegmentId = transcriptSegments[i].id;
        break;
      }
    }

    if (currentSegmentId !== activeAudioSegmentId) {
      setActiveAudioSegmentId(currentSegmentId);
    }
  }, [currentTime, transcriptSegments, activeAudioSegmentId]);

  // Scroll to active audio segment
  useEffect(() => {
    if (!activeAudioSegmentId) return undefined;

    const scrollToElement = () => {
      const element = segmentRefs.current[activeAudioSegmentId];
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest',
        });
        return true;
      }
      return false;
    };

    // Try immediate scroll first
    if (scrollToElement()) return undefined;

    // If element not found, wait for render and try again
    const timeoutId = setTimeout(scrollToElement, 16);
    return () => clearTimeout(timeoutId);
  }, [activeAudioSegmentId]);

  /* ---------------------------------- JSX ---------------------------------- */
  if (!ac) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center">
        <div className="rounded-full bg-gray-50 p-4 mb-4">
          <SpeakerWaveIcon className="h-8 w-8 text-gray-300" />
        </div>
        <h3 className="text-lg font-medium text-gray-800 mb-2">
          No Audio Conversation
        </h3>
        <p className="text-sm text-gray-500 max-w-sm">
          The audio conversation data could not be loaded.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden max-w-5xl mx-auto">
      {/* Header */}
      <PreviewHeader conversation={ac} />

      {/* Digest Summary */}
      {digest?.work_log_entry && (
        <div className="px-6 py-4 border-b border-gray-50 bg-gray-25">
          <h4 className="text-xs font-medium text-gray-600 uppercase tracking-wide mb-2">
            Summary
          </h4>
          <p className="text-sm text-gray-700 leading-relaxed">
            {digest.work_log_entry}
          </p>
        </div>
      )}

      {/* Transcript Area */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Search Bar */}
          <SearchBar
            query={searchQuery}
            onChange={setSearchQuery}
            onNext={gotoNextMatch}
            onPrev={gotoPreviousMatch}
            hasResults={transcriptSegments.length > 0}
          />

          {/* Audio controls */}
          {ac && ac.audio_path && (
            <div className="border-b border-gray-50">
              <ConversationAudioControls
                conversation={{
                  id: ac.id,
                  audio_path: ac.audio_path,
                  duration: ac.duration,
                }}
                onReady={(ws: WaveSurfer) => {
                  wavesurferRef.current = ws;
                  // track play / pause events to reflect accurate UI state
                  ws.on('play', () => setAudioIsPlaying(true));
                  ws.on('pause', () => setAudioIsPlaying(false));
                  ws.on('finish', () => setAudioIsPlaying(false));
                }}
                onTimeUpdate={(time: number) => setCurrentTime(time)}
              />
            </div>
          )}

          {/* Transcript List */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-400 text-sm animate-pulse">
                  Loading transcript...
                </div>
              </div>
            )}
            {!isLoading && transcriptSegments.length === 0 && (
              <div className="flex items-center justify-center h-32">
                <div className="text-gray-400 text-sm">
                  No transcript segments available
                </div>
              </div>
            )}

            {!isLoading && transcriptSegments.length > 0 && (
              <TranscriptList
                segments={transcriptSegments}
                searchQuery={searchQuery}
                matchIds={matchIds}
                matchRanges={matchRanges}
                playingSegmentId={activeAudioSegmentId}
                audioIsPlaying={audioIsPlaying}
                onSegmentInteract={handleSegmentInteract}
                segmentRefs={segmentRefs}
              />
            )}
          </div>

          {/* Search Navigation */}
          {searchQuery && matchIds.length > 0 && (
            <div className="px-6 py-3 border-t border-gray-50 bg-gray-25 flex justify-between items-center">
              <span className="text-xs text-gray-500 font-medium">
                {activeMatchIndex + 1} of {matchIds.length} matches
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={gotoPreviousMatch}
                  disabled={matchIds.length <= 1}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-medium bg-white hover:bg-gray-50 border border-gray-200 rounded-md transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={gotoNextMatch}
                  disabled={matchIds.length <= 1}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
